import { effect, Injectable, OnDestroy, signal } from "@angular/core";
import { DataAccessService } from "../data-access/data-access.service";

import {
  TBResetAction,
  TaskSource,
  TaskState,
  TaskType,
} from "../../models/core";
import {
  TaskInfoService,
  TBCommunications,
} from "../task-info/task-info.service";
import { DataRepositoryService } from "../data-repository/data-repository.service";
import {
  CommunicationDataStream,
  MovingTaskEntry,
  SelectionOption,
  TaskMoveCommand,
  PullBackTaskCommand,
  SelectedTBRowKPI,
} from "../../entity/utility-interfaces";
import {
  CommandType,
  RouteState,
  TBDataSteamType,
  TBTabName,
} from "../../entity/enums";
import { KeyValue, TitleCasePipe } from "@angular/common";
import { ConnectionService } from "../connection/connection.service";
import { AppConstants } from "../../entity/constants";
import { DataFormatService } from "../format/data-format.service";
import { Subject, Subscription, take } from "rxjs";
import { IUPPTasksMap } from "../../models/task-unplannedpickup";
import { TaskSelectPipe } from "../../pipe/task-select/task-select-pipe.pipe";
import { IUnloadedTasksMap } from "../../models/task-unloaded";
import { IPlannedPBMTasksMap, ITasksMap } from "../../models/tasks-planned";
import { HttpStatusCode } from "@angular/common/http";
import { Router } from "@angular/router";
import { Navigation } from "../../entity/navigation";
import { TaskInfoFormatterService } from "../task-info/task-info-formatter.service";
import { ErrorMessage } from "../../entity/error.const";
import { BLRoutesList } from "../../models/kpi-details";

/** Union of all task-bank record types that can be selected for movement. */
type TaskRecord = ITasksMap | IUPPTasksMap | IPlannedPBMTasksMap | IUnloadedTasksMap;

/** Minimum properties required to build a MovingTaskEntry for tasks (non-item). */
interface TaskObjectSource {
  business_location_id: string;
  route_id?: string;
  task_id?: string;
  task_type?: string;
  task_state?: string;
  taskStateTab: string;
  source?: string;
}

/** Route list API response from the V4 routes endpoint. */
interface RouteListResponse {
  routes: BLRoutesList[];
}

/** Result emitted by the taskMovementSuccess subject. */
interface TaskMovementResult {
  success: boolean;
  message: string;
}

enum BLState {
    Default,
    UserChanged,
    TaskSelected
}

@Injectable({
  providedIn: "root",
})
export class TaskMovementService implements OnDestroy {
  constructor(
    public dataAccessService: DataAccessService,
    public dataFormatService: DataFormatService,
    public dataRepositoryService: DataRepositoryService,
    public taskInfoService: TaskInfoService,
    private readonly taskInfoFormatterService: TaskInfoFormatterService,
    public titleCasePipe: TitleCasePipe,
    private connectionService: ConnectionService,
    private tBCom: TBCommunications,
    private taskSelectPipe: TaskSelectPipe,
    public router: Router
  ) {
    //subscribe to tab switching observable
    this.taskMoveServiceSubs$.push(
      this.dataAccessService.isTBTabSwitched$.subscribe(
        (isTabSwitched: boolean) => {
          if (isTabSwitched) {
            this.resetTaskMovement();
          }
        }
      )
    );
    this.taskMoveServiceSubs$.push(
      this.dataAccessService.isFavBLChanged$.subscribe(
        (isFavBLChanged: boolean) => {
          if (isFavBLChanged) {
            this.resetTaskMovement();
          }
        }
      )
    );

    //subscribe to global filter observable
    this.taskMoveServiceSubs$.push(
      this.dataAccessService.fitlerApplied$.subscribe((data) => {
        if (data) {
          //Clear the existing task movement selections
          this.resetTaskMovement();
        }
      })
    );

    //subscribe to tbCommunication service
    this.taskMoveServiceSubs$.push(
      this.tBCom.dataStream$.pipe().subscribe((res) => {
        if (res) {
          this.extractDataStream(res);
        }
      })
    );

    //effects 
    effect(()=>{
      //act on the signal 
      let selectRowCount = this.taskmoveCount();
      if(selectRowCount){
        this.setSelectedRowKPI();
      }
    })
  }

  //class variables
  movedTaskBLId = "";
  selectedOptionsForTaskMove: TaskRecord[] = [];
  public taskmoveCount = signal(0);
  allowedTaskMoveData: TaskRecord[];
  resetBtnAction: string = "";
  taskMovementEnablerTab: string = "";
  isTaskMovementDone: boolean = false;
  isTaskMovementTriggered: boolean = false;
  isSelectedAllTasksTriggered: boolean = false;
  isMoveToOnRouteAllowed: boolean = true;
  isTargetBLSelectionAllowed: boolean = true;
  isResetDisabled: boolean = true;
  isSubmitDisabled: boolean = true;
  isPullbackDisabled: boolean = true;
  isUpdateStateDisable:boolean = true;
  isTargetBLSelectionDisabled: boolean = true;
  isTargetRouteSelectionDisabled: boolean = true;
  blAllRoutes: RouteListResponse = { routes: [] };
  taskState = TaskState;
  targetRouteSelectionList: SelectionOption[] = [];
  selectedTargetRoute: SelectionOption | undefined;
  selectedTargetBL: SelectionOption | undefined;
  businessLocationsList: SelectionOption[] = [];
  selectedTaskKPI: KeyValue<keyof SelectedTBRowKPI, string| number>[] = [];// ** This should be trransformed into computed signal with Angular 20 upgrade
  showSelectedTaskKPI: boolean = false;
  isRotated = false;
  
  
  /**
   * isSelectedAll flag is used to acknoledge the view element (checkbox)
   * during a UI refresh to retain the selection
   */
  isSelectedAll: boolean = false;


  //subscription variables
  taskMoveServiceSubs$: Subscription[] = [];

  // task movement successful
  taskMovementSuccess: Subject<TaskMovementResult> = new Subject();
  resetButtonClicked: Subject<boolean> = new Subject();


  // Tracks how the BL was selected (default, user-changed, or task-selected)
  blState: BLState = BLState.Default;


  //lifecycle hooks
  ngOnDestroy(): void {
    this.taskMoveServiceSubs$?.forEach((subs: Subscription) => {
      subs?.unsubscribe();
    });
  }

  //effect methods

  //class methods

  /**
   * Methods enables the task movement and check if highlighting of task marker 
   * in Map view is required.
   * @param event Input event contains selected tasks information
   */
  onTaskSelection(event: TaskRecord) {
    this.enableTaskMovement(event);
    this.blState = BLState.TaskSelected;
    if((this.router.url === Navigation.MapLeaflet || this.router.url === Navigation.Landing) && event.taskStateTab.toLowerCase() === TBTabName.Planned){
      // Highlight every selected planned task not already on the map. Keying on
      // highlightedTaskOnMap (the source of truth) keeps this idempotent, so a
      // stale row-copy isSelectedByMap flag can't toggle a highlighted pin back off.
      this.selectedOptionsForTaskMove
        .filter(task => task.isMovementAllowed && 'task_id' in task && !this.dataRepositoryService.highlightedTaskOnMap.has(task.task_id))
        .forEach(task => this.updateHighlightingTasks(task as ITasksMap));
    }
  }

  /**
   * This method is called when user click on select all
   * @param event event info of select all checkbox
   */
  onSelectAllToggle(event: { checked: boolean }) {
    this.selectedOptionsForTaskMove = this.selectedOptionsForTaskMove.filter(task => task.isMovementAllowed);
    this.taskmoveCount.set(this.selectedOptionsForTaskMove.filter(f=>f.isMovementAllowed).length);

    if (event.checked) {
      this.isSelectedAll = true;
      this.enableBulkTaskMovement();
    } else {
      this.resetTaskMovement();
    }
  }

  /**
   * Loads master business location data for the Unplanned Pickup tab.
   */
  loadMasterBLDataForUpp(): void {
    this.dataAccessService.getBLMasterData(false)
      .pipe(take(1))
      .subscribe({
        next: (blData: SelectionOption[]) => {
          this.businessLocationsList = [...blData];
          this.setSelectedTargetBL(this.movedTaskBLId);
        },
        error: () => {
          this.businessLocationsList = [];
        }
      });
  }

  /**
   * Checks whether the selected business location is accessible to the current user
   * based on their role-based permissions.
   * @param targetBLId The business location ID to check.
   * @returns True if the BL is accessible, false otherwise.
   */
  private isBLAccessibleForUser(targetBLId: string): boolean {
    return this.dataRepositoryService.businessLocationListMap.has(targetBLId);
  }

  /**
   * Sets the selected target business location from the current dropdown list.
   * Updates the selectedTargetBL property with the matching BL details.
   * @param targetBLId The ID of the business location to select.
   */
  private setSelectedTargetBL(targetBLId: string): void {
    const selectedBL = this.businessLocationsList.find((bl) => bl.id === targetBLId);
    if (!selectedBL) {
      return;
    }

    this.selectedTargetBL = {
      id: selectedBL.id,
      value: selectedBL.value,
    };
  }

  /**
   * Disables route selection and updates the UI accordingly.
   */
  private disableRouteSelection(): void {
    this.isTargetRouteSelectionDisabled = true;
    this.targetRouteSelectionList = [];
    this.selectedTargetRoute = {} as SelectionOption;
    this.blAllRoutes = { routes: [] };
    // Allow task movement without route selection when selected BL is outside user route access.
    this.isSubmitDisabled = false;
    this.isPullbackDisabled = true;
    this.dataRepositoryService.taskMovementData.route_id = "";
  }

  /**
   * Enables route selection for accessible BL and updates the UI accordingly.
   * @param targetBLId The ID of the target business location.
   * @param isSubmitDisabled Flag indicating whether the submit button should be disabled.
   */
  private enableRouteSelectionForAccessibleBL(targetBLId: string, isSubmitDisabled = true): void {
    this.isTargetRouteSelectionDisabled = false;
    this.getRouteListForBL(targetBLId);
    this.isSubmitDisabled = isSubmitDisabled;
    this.isPullbackDisabled = false;
  }

  /**
   * Handles target BL selection for the Unplanned Pickup tab.
   * Validates BL accessibility and enables/disables route selection accordingly.
   * @param targetBLId The ID of the selected target business location.
   */
  private handleUnplannedTargetBLSelection(targetBLId: string): void {
    if (!this.isBLAccessibleForUser(targetBLId)) {
      this.disableRouteSelection();
      return;
    }
    // Preserve existing behavior for Unplanned tab after BL selection.
    this.enableRouteSelectionForAccessibleBL(targetBLId, false);
  }

  /**
   * Handles default target BL selection for non-Unplanned tabs.
   * Fetches route list for the selected BL and enables route selection UI.
   * @param targetBLId The ID of the selected target business location.
   */
  private handleDefaultTargetBLSelection(targetBLId: string): void {
    this.getRouteListForBL(targetBLId);
    this.isSubmitDisabled = true;
    this.isPullbackDisabled = false;
    this.isTargetRouteSelectionDisabled = false;
  }

  /**
   * Method updates the collection of task for movement and calls
   * method for taskmovement formation of payload data.
   * @param selectedRows list of all the selected task for movement
   */
  taskSelectionFromMapPolygon(selectedRows: TaskRecord[]){
    if(selectedRows?.length){
      //check for already selected task 
      if(this.taskInfoService.selectedTabName.toLowerCase()===TBTabName.Unloaded.toLowerCase()){
        selectedRows.forEach((task) => {
          const itemId = 'item_id' in task ? task.item_id : undefined;
          if(this.selectedOptionsForTaskMove?.findIndex(f => 'item_id' in f && f.item_id === itemId) === -1){
            this.selectedOptionsForTaskMove = this.selectedOptionsForTaskMove.concat(task);
            this.taskmoveCount.set(this.selectedOptionsForTaskMove.filter(f=>f.isMovementAllowed).length);
          }
        });
      }else{
        selectedRows.forEach((task) => {
          const taskId = 'task_id' in task ? task.task_id : undefined;
          if(this.selectedOptionsForTaskMove?.findIndex(f => 'task_id' in f && f.task_id === taskId) === -1){
            this.selectedOptionsForTaskMove = this.selectedOptionsForTaskMove.concat(task);
            this.taskmoveCount.set(this.selectedOptionsForTaskMove.filter(f=>f.isMovementAllowed).length);
          }
        });
      }
      this.setSelectAll();
      //1. enable task movement- update the TB tab 
      this.enableBulkTaskMovement();
    }
  }

  /**
   * this methods checks if selected tasks for task move are all tasks in tb
   * then it sets select all true or false to handle custom sort
   */
  setSelectAll() {
    if (this.allowedTaskMoveData.length === this.taskmoveCount()) {
      this.isSelectedAll = true;
    } else {
      this.isSelectedAll = false;
    }
  }
  /**
   * This method is called in case of select all is clicked or bulk selection in map polygon
   * it performs task movement of all selected tasks
   */
  enableBulkTaskMovement() {
    const selectedTabName = this.taskInfoService.selectedTabName.toLowerCase();
    const eventTabName = this.selectedOptionsForTaskMove[0]?.taskStateTab.toLowerCase();
    if (selectedTabName !== eventTabName) {
      this.taskInfoService.selectedTabName = this.selectedOptionsForTaskMove[0]?.taskStateTab;
    }
    switch (selectedTabName) {
      case TBTabName.Planned.toLowerCase():
      case TBTabName.Unplannedpickup.toLowerCase():
        //2. Iterate through the task list
        this.selectedOptionsForTaskMove?.forEach((task) => {
          if (
            (this.router.url === Navigation.MapLeaflet ||
              this.router.url === Navigation.Landing) &&
            task.taskStateTab.toLowerCase() === TBTabName.Planned
          ) {
            const highlightTaskId = 'task_id' in task ? task.task_id : '';
            if(highlightTaskId && !this.dataRepositoryService.highlightedTaskOnMap.has(highlightTaskId)){
              this.updateHighlightingTasks(task as ITasksMap);
            }
          }
          // Validation to check if task already exists for movement.
          if (this.canMoveTask(task)) {
            this.movedTaskBLId = task?.business_location_id;
            this.formTaskMovementObject(task as TaskObjectSource);
            this.handleFavListForUnplannedPickup();
          }
        });
        break;
      case TBTabName.Unloaded.toLowerCase():

        //2. Iterate through the task list
        this.selectedOptionsForTaskMove?.forEach((task) => {
          // Validation to check if task already exists for movement.
          const unloadedItemId = 'item_id' in task ? task.item_id : undefined;
          if (
            unloadedItemId !== undefined &&
            ![...this.dataRepositoryService.movingTasksMap.values()]
              .map((m) => m.item_id)
              ?.includes(unloadedItemId)
          ) {
            this.movedTaskBLId = task.business_location_id;
            this.formatItemMovementObject(task as IUnloadedTasksMap);
          }
        });

        break;
      case TBTabName.PlannedPBM.toLowerCase():
      case TBTabName.PushToCloud.toLowerCase():
        //2. Iterate through the task list
        this.selectedOptionsForTaskMove?.forEach((task) => {
          // Validation to check if task already exists for movement.
          // if (!this.isTaskInMovingMap(task.task_id)) {
          if (this.canMoveTask(task)) {
            this.movedTaskBLId = task.business_location_id;
            this.formTaskMovementObject(task as TaskObjectSource);
          }

        });

        break;
    }
    //3. Check the target route and fetch if needed
    //get the BL route list for target route selection
    this.getTargetRouteListForBL();
    this.blState = BLState.TaskSelected;
  }
  
  /**
   * Methods gets the selected task's information and an object for task movement
   * It checks if target route list if available or else fetches from server and
   * also business location list if selected tab is Unplanned pickup
   * @param taskData selected task object
   */
  enableTaskMovement(taskData: TaskRecord){
    let selectedTabName = this.taskInfoService.selectedTabName.toLowerCase();
    const eventTabName = taskData.taskStateTab.toLowerCase();
    if (selectedTabName !== eventTabName) {
      this.taskInfoService.selectedTabName = taskData.taskStateTab.toLowerCase();
      selectedTabName = taskData.taskStateTab.toLowerCase();
    }
    if(this.selectedOptionsForTaskMove.length){
      this.taskmoveCount.set(this.selectedOptionsForTaskMove.filter(f=>f.isMovementAllowed).length);
    }
    this.setSelectAll();
    if (
      selectedTabName === TBTabName.Unplannedpickup ||
      selectedTabName === TBTabName.Planned
    ) {
      if(!this.dataRepositoryService.masterBusinessLocationsList.length)this.loadMasterBLDataForUpp();
      this.plannedUPPTaskMovement(taskData as IUPPTasksMap);
    } else if (selectedTabName === TBTabName.PlannedPBM) {
      this.handlePlannedPBMTasks(taskData as IPlannedPBMTasksMap);
    } else if(selectedTabName === TBTabName.PushToCloud){
      this.onCloudTaskSelection(taskData as IUPPTasksMap);
    } else if(selectedTabName === TBTabName.PreviousDayOrder){
      this.handlePreviousDayTasks(taskData as IPlannedPBMTasksMap);
    }
  }

  
  /**
  * task movement of Previous Day tasks handled
  * @param event Input event contains selected tasks information
  */
  handlePreviousDayTasks(event: IPlannedPBMTasksMap) {
    if (!this.isTaskInMovingMap(event.task_id)) {
      this.selectedOptionsForTaskMove?.forEach(
        (selectedTask) => {
          if (this.canMoveTask(selectedTask)) {
            this.movedTaskBLId = event.business_location_id;
            this.formTaskMovementObject(selectedTask as TaskObjectSource);
          }
        }
      );
    }
  }

  /**
   * Method handles the selected task for movement, 
   * checks its validaty, and update service for movement actions  
   * @param plannedData task object
   */
  handleTaskMoveWithClick(plannedData: ITasksMap){
    if(plannedData.isMovementAllowed){
      //update the collection of task movement's selected task
      if(this.selectedOptionsForTaskMove?.findIndex(f => 'task_id' in f && f.task_id===plannedData?.task_id)==-1){
        this.selectedOptionsForTaskMove = this.selectedOptionsForTaskMove.concat(plannedData);
        this.taskmoveCount.set(this.selectedOptionsForTaskMove.filter(f=>f.isMovementAllowed).length);
        this.enableTaskMovement(plannedData);
      }
      else{
        // Map-pin re-click deselects: remove from selection and moving map explicitly
        this.handleMovementForTaskDeselection(plannedData);
        this.onTaskUnselection(plannedData.task_id);
      }
    }
  }

  /**
   * Method handles the task deselection and updated required collection 
   * used in task movement
   * @param task task object which is deselected
   */
  handleMovementForTaskDeselection(task: TaskRecord){
    let taskIndex = 0;
    if(this.taskInfoService.selectedTabName.toLowerCase()===TBTabName.Unloaded.toLowerCase()){
      const itemId = 'item_id' in task ? task.item_id : undefined;
      taskIndex = this.selectedOptionsForTaskMove.findIndex(
        (f) => 'item_id' in f && f.item_id === itemId
      );
    }else{
      const taskId = 'task_id' in task ? task.task_id : undefined;
      taskIndex = this.selectedOptionsForTaskMove.findIndex(
        (f) => 'task_id' in f && f.task_id === taskId
      );
    }
    if (taskIndex != -1) {
      this.selectedOptionsForTaskMove.splice(taskIndex, 1);
      const taskList = [...this.selectedOptionsForTaskMove];
      this.selectedOptionsForTaskMove = [...taskList];
      this.taskmoveCount.set(this.selectedOptionsForTaskMove.filter(f=>f.isMovementAllowed).length);
      this.isSelectedAll = this.isSelectedAll ? false : this.isSelectedAll;
    }
  }

  /**
   * Method handle the highlighting of the task in map component,
   * it sets the collection of highlighting tasks and triggers the event 
   * to Map component.
   * @param taskData selected task object
   */
  updateHighlightingTasks(taskData: ITasksMap){
    // Use the object stored in plannedTbCollection as the single source of truth
    // for the map-pin highlight. The task-bank table re-renders by rebuilding its
    // rows from plannedTbCollection (setPlannedData spreads fresh copies), so the
    // flag must be persisted on the collection object - in addition to the passed
    // reference - to survive re-renders (select-all, sorting, map selection).
    const collectionTask =
      this.dataRepositoryService.plannedTbCollection.get(taskData.task_id) ?? taskData;
    const isHighlighted = !collectionTask.isSelectedByMap;
    collectionTask.isSelectedByMap = isHighlighted;
    taskData.isSelectedByMap = isHighlighted;
    if(isHighlighted) {
      this.dataRepositoryService.highlightedTaskOnMap.set(taskData.task_id,taskData.task_id);
    } else {
      this.dataRepositoryService.highlightedTaskOnMap.delete(taskData.task_id);
    }
    this.taskInfoService.onSelectTaskFromPlannedTable({
      isSelectedFromPlannedTable: isHighlighted,
      taskId: taskData.task_id,
      routeId: taskData.route_id
    })
  }

  /**
   * task movement of unplanned pickup and planned handled
   * @param event Input event contains selected tasks information
   */
  plannedUPPTaskMovement(event: IUPPTasksMap) {
    if (!this.isTaskInMovingMap(event.task_id)) {
      this.selectedOptionsForTaskMove?.forEach((selectedTask) => {
        if (this.canMoveTask(selectedTask)) {
          this.movedTaskBLId = event.business_location_id;
          this.formTaskMovementObject(selectedTask as TaskObjectSource);
          this.handleFavListForUnplannedPickup();
        }
      });
      //get the BL route list for target route selection 
      this.getTargetRouteListForBL()
    }
  }
  /**
   * task movement of Planned PBM handled
   * @param event Input event contains selected tasks information
   */
  handlePlannedPBMTasks(event: IPlannedPBMTasksMap) {
    if (!this.isTaskInMovingMap(event.task_id)) {
      this.selectedOptionsForTaskMove?.forEach(
        (selectedTask) => {
          if (this.canMoveTask(selectedTask)) {
            this.movedTaskBLId = event.business_location_id;
            this.formTaskMovementObject(selectedTask as TaskObjectSource);
            this.handleFavListForPlannedPBM();
          }
        }
      );
      this.getTargetRouteListForBL();
    }
  }
  /**
   *
   * @param taskId task id of movable task
   * @returns
   */
  isTaskInMovingMap(taskId: string): boolean {
    return [...this.dataRepositoryService.movingTasksMap.values()].some(
      (movingTask) => movingTask.task_id === taskId
    );
  }

  /**
   *
   * @param selectedTask contains selected task information
   * @returns selectedtask movable or not checks
   */
  canMoveTask(selectedTask: TaskRecord): boolean {
    const taskId = 'task_id' in selectedTask ? selectedTask.task_id : '';
    return !!selectedTask.isMovementAllowed && !this.isTaskInMovingMap(taskId);
  }
  /**
   * handle fav list of unplanned pickup
   */
  handleFavListForUnplannedPickup() {
    if (
      this.taskInfoService.selectedTabName.toLowerCase() ===
        TBTabName.Unplannedpickup &&
      this.taskInfoService.selectedUnplannedFavList?.id !== "default"
    ) {
      this.reformatOtherTasks();
    }
  }
  /**
   * handle fav list of planned pbm
   */
  handleFavListForPlannedPBM() {
    if (this.taskInfoService.selectedPlannedFavList?.id !== "default") {
      this.reformatPlannedPBMTasks();
    }
  }

  onUnloadedItemSelection(event: IUnloadedTasksMap) {
    if (
      this.taskInfoService.selectedTabName.toLowerCase() !==
      event.taskStateTab.toLowerCase()
    ) {
      this.taskInfoService.selectedTabName = event.taskStateTab;
    }

    if (
      [...this.dataRepositoryService.movingTasksMap.values()].findIndex(
        (selectedTasks) => selectedTasks.item_id === event?.item_id
      ) === -1
    ) {

      if (this.selectedOptionsForTaskMove.length) {
        this.taskmoveCount.set(this.selectedOptionsForTaskMove.filter(f=>f.isMovementAllowed).length);
      }
      //iterate throught each task available in selectedOptionsForTaskMove
      this.selectedOptionsForTaskMove?.forEach(
        (selectedTask) => {
          const selItemId = 'item_id' in selectedTask ? selectedTask.item_id : undefined;
          if (
            selItemId !== undefined &&
            ![...this.dataRepositoryService.movingTasksMap.values()]
              .map((m) => m.item_id)
              ?.includes(selItemId)
          ) {
            this.movedTaskBLId = event.business_location_id;
            this.formatItemMovementObject(selectedTask as IUnloadedTasksMap);
          }
        }
      );
      //get the BL route list for target route selection 
      this.getTargetRouteListForBL()
    }
    this.setSelectAll();
  }
/**
 * to move selected task
 * @param event selected task information
 */
  onCloudTaskSelection(event: IUPPTasksMap) {
    if (
      this.taskInfoService.selectedTabName.toLowerCase() !==
      event.taskStateTab.toLowerCase()
    ) {
      this.taskInfoService.selectedTabName = event.taskStateTab;
    }
    if (
      [...this.dataRepositoryService.movingTasksMap.values()].findIndex(
        (selectedTasks) => selectedTasks.task_id === event?.task_id
      ) === -1
    ) {
      //iterate throught each task available in selectedOptionsForTaskMove
      this.selectedOptionsForTaskMove?.forEach(
        (selectedTask) => {
          const cloudSelTaskId = 'task_id' in selectedTask ? selectedTask.task_id : undefined;
          if (
            cloudSelTaskId !== undefined &&
            ![...this.dataRepositoryService.movingTasksMap.values()]
              .map((m) => m.task_id)
              ?.includes(cloudSelTaskId)
          ) {
            this.movedTaskBLId = event.business_location_id;
            this.formTaskMovementObject(selectedTask as TaskObjectSource);
          }
        }
      );
      //get the BL route list for target route selection 
      this.getTargetRouteListForBL();
    }
  }
  /**
   * Method gets unselected task and update the collection for task movement
   * @param event Input event contains unselected tasks information
   */
  onTaskUnselection(id: string) {
    this.removeSelectedTask(id);
  }

  /**
   * Method removes the task from moveTask list
   */
  removeSelectedTask(id: string) {
    this.dataRepositoryService.movingTasksMap.delete(id);
    this.selectedOptionsForTaskMove = this.selectedOptionsForTaskMove.filter(
      (task) => (!('task_id' in task) || task.task_id !== id) && (!('item_id' in task) || task.item_id !== id)
    );
    this.taskmoveCount.set(
      this.selectedOptionsForTaskMove.filter((task) => task.isMovementAllowed).length
    );
    if (this.dataRepositoryService.movingTasksMap.size === 0) {
      this.isTaskMovementTriggered = false;
      this.movedTaskBLId = "";
      this.resetTaskMovement();
    } else {
      this.formatTargetRouteList();
    }
    //reformat the other task based on the unselection of task
    if (
      this.taskInfoService.selectedTabName.toLowerCase() ===
        TBTabName.Unplannedpickup &&
      this.taskInfoService.selectedUnplannedFavList?.id !== "default"
    ) {
      this.reformatOtherTasks();
    }
    if (
      this.taskInfoService.selectedTabName.toLowerCase() ===
        TBTabName.PlannedPBM &&
      this.taskInfoService.selectedPlannedFavList?.id !== "default"
    ) {
      this.reformatPlannedPBMTasks();
    }
    if (this.taskInfoService.selectedTabName.toLowerCase() === TBTabName.Planned) {
      this.updateControlStates();
    }
  }

  /**
   * Methods forms the task movement object and sets the task movement controls in task bank
   * @param movingTask Object of task which is selected for the movement
   */
  formTaskMovementObject(movingTask: TaskObjectSource) {
    const taskObject = this.createTaskObject(movingTask);
    this.dataRepositoryService.movingTasksMap.set(
      movingTask.task_id ?? '',
      taskObject
    );
    this.isTaskMovementTriggered = true;

    this.updateControlStates();
  }

  getTargetRouteListForBL() {
    if (!this.isSelectedAllTasksTriggered) {
      //get the all route list for target route selection and 
      // Added check for the blstate changed to handle the case if 
        // user has select task and changed the bl dropdown after that he selected the task again which
        // end up setting the bl dropdown default and doesn't update the route list.
      if (this.blState === BLState.UserChanged || !this.blAllRoutes?.routes?.length) {
        //API call to fetch all routes for given BL
        //pick the first task BL id, as all tasks have same BL id
        const selectedTask = this.selectedOptionsForTaskMove?.[0];
        const businessLocationId = selectedTask?.business_location_id ?? this.movedTaskBLId;
        if (businessLocationId) {
          this.getRouteListForBL(businessLocationId);
        }
      } else {
        this.formatTargetRouteList();
      }
    }
  }

  /**
   * Methods forms the items movement object and sets the task movement controls in task bank
   * @param movingItem Object of item which is selected for the movement
   */
  formatItemMovementObject(movingItem: IUnloadedTasksMap) {
    const itemObject = this.createItemObject(movingItem);
    this.dataRepositoryService.movingTasksMap.set(
      movingItem.item_id,
      itemObject
    );
    this.isTaskMovementTriggered = true;

    this.updateControlStates();

  }
  /**
   * create task objects
   */
  createTaskObject(movingTask: TaskObjectSource): MovingTaskEntry {
    return {
      bl_id: movingTask.business_location_id,
      route_id: movingTask.route_id,
      task_id: movingTask.task_id,
      task_type: movingTask.task_type ?? "",
      task_state: movingTask.task_state ?? "",
      taskbankTab: movingTask.taskStateTab,
      source: movingTask.source ?? "",
    };
  }
  /**
   * create item objects
   */
  createItemObject(movingItem: IUnloadedTasksMap): MovingTaskEntry {
    return {
      bl_id: movingItem.business_location_id,
      route_id: movingItem.route_id,
      item_id: movingItem.item_id,
      item_state: movingItem.item_state ?? "",
      taskbankTab: movingItem.taskStateTab,
      source: movingItem.source ?? "",
    };
  }

  /**
   * Extracts unique route IDs from `movingTasksMap`.
   * If any moved task/item does not have a `route_id`, returns an empty array.
   */
  getUniqueRouteIdsFromMovingTasksMap(): string[] {
    const movedValues = [...this.dataRepositoryService.movingTasksMap.values()];
    if (!movedValues?.length) {
      return [];
    }

    const uniqueRouteIds = new Set<string>();
    for (const moved of movedValues) {
      const routeId = (moved?.route_id ?? '').toString().trim();
      if (!routeId) {
        return [];
      }
      uniqueRouteIds.add(routeId);
    }
    return [...uniqueRouteIds];
  }

  /**
   * task movement tasks checks
   */
  updateControlStates() {
    if (this.selectedOptionsForTaskMove.length) {
      this.isResetDisabled = false;
      this.isTargetRouteSelectionDisabled = false;
      const selectedTab = this.taskInfoService.selectedTabName.toLowerCase();

      switch (selectedTab) {
        case TBTabName.Unplannedpickup:
          this.isTargetBLSelectionDisabled = false;
          this.isUpdateStateDisable = false;
          this.handleTaskMovement();
          break;
        case TBTabName.PlannedPBM:
          this.isPullbackDisabled = Boolean(this.selectedTargetRoute?.id);
          this.isTargetBLSelectionDisabled = false;
          this.isUpdateStateDisable = false;
          this.handleTaskMovement();
          break;
        case TBTabName.Planned:
          this.handlePlannedTasks();
          break;
        case TBTabName.PreviousDayOrder:
          this.isUpdateStateDisable = false;
          break;
        default: break;
      }
    }
  }

  handlePlannedTasks() {
    if (this.isPBMSource(this.selectedOptionsForTaskMove)) {
      this.isPullbackDisabled = this.isTargetBLSelectionDisabled = Boolean(this.selectedTargetRoute?.id);
      this.handleTaskMovement();
    }
    else { // reset pullback & BL selection if task source is non-PBM
      const selectedBusinessLocation = this.dataRepositoryService.selectedBusinessLocation;
      this.selectedTargetBL = {
        id: selectedBusinessLocation,
        value: this.businessLocationsList
          .find(bl => bl.id === selectedBusinessLocation)?.value ?? ''
      };
      this.isPullbackDisabled = this.isTargetBLSelectionDisabled = true;
    }
  }
  /**
   * Initializes the business locations list based on the active tab.
   * For Unplanned Pickup: reads from dataRepositoryService.masterBusinessLocationsList.
   * For other tabs: builds from dataRepositoryService.businessLocationListMap.
   * Only initializes once per task movement session (skips if already populated).
   */
  private initBusinessLocationsList(): void {
    if (this.businessLocationsList.length) {
      return;
    }

    const isUnplannedPickupTab =
      this.taskInfoService.selectedTabName.toLowerCase() === TBTabName.Unplannedpickup.toLowerCase();

    if (isUnplannedPickupTab) {
      this.businessLocationsList = [...this.dataRepositoryService.masterBusinessLocationsList];
    } else if (this.dataRepositoryService?.businessLocationListMap.size) {
      this.businessLocationsList = [
        ...this.dataRepositoryService.businessLocationListMap.values(),
      ].reduce((acc: SelectionOption[], bl) => {
        acc.push({ id: bl.business_location_id, value: bl.name });
        return acc;
      }, []);
    }
  }

  /**
   * Handles BL list initialization and target BL selection for task movement.
   * Delegates BL list creation to initBusinessLocationsList() which resolves
   * the correct source based on the active tab.
   */
  handleTaskMovement() {
    this.selectedTargetBL = {} as SelectionOption;

    this.initBusinessLocationsList();

    if (this.dataRepositoryService.selectedBusinessLocation !== "") {
      this.setSelectedTargetBL(this.movedTaskBLId);
      if (this.selectedTargetBL?.id) {
        //check the change of BL in dropdown than business location selected in DP header and then call routes for that BL
        if(this.checkFavListSelected()){
          if (
            this.selectedTargetBL.id !== "" &&
            this.dataRepositoryService.selectedBusinessLocation !==
              this.selectedTargetBL.id
          ) {
            this.getRouteListForBL(this.selectedTargetBL?.id);
          }
        }
      }
    }
  }


  /**
   * * Checks if the currently selected tab has the default selection.
   * @returns True if the active tab selected favorite list id is "default", otherwise false.
  */
  checkFavListSelected() {
    const tabName = this.taskInfoService.selectedTabName.toLowerCase();
    return (tabName === TBTabName.Unplannedpickup && this.taskInfoService.selectedUnplannedFavList?.id === "default") ||
      (tabName === TBTabName.PlannedPBM && this.taskInfoService.selectedPlannedFavList?.id === "default");
  }

  /**
   * Methos reformats the isMovementAllowed property of other tasks which belong to different BL
   * in case of Favorite BL is selected in UPP tab.
   * In order to disable the selection of other BL task while one specific BL task is already selected.
   */
  reformatOtherTasks() {
    //UPP tab
    let uppTasks: IUPPTasksMap[] = [
      ...this.dataRepositoryService.unplannedTbCollection.values(),
    ];
    uppTasks.forEach((uppTask: IUPPTasksMap) => {
      uppTask.isMovementAllowed = this.taskSelectPipe.transform(
        uppTask,
        TBTabName.Unplannedpickup,
        this.movedTaskBLId
      );
    });
  }

  /**
   * Methos reformats the isMovementAllowed property of other tasks which belong to different BL
   * in case of Favorite BL is selected in UPP tab.
   * In order to disable the selection of other BL task while one specific BL task is already selected.
   */
  reformatPlannedPBMTasks() {
    //PlannedPBM tab
    let plannedpbmTasks: IPlannedPBMTasksMap[] = [
      ...this.dataRepositoryService.plannedPbmTbCollection.values(),
    ];
    plannedpbmTasks.forEach((plannedpbmTask: IPlannedPBMTasksMap) => {
      plannedpbmTask.isMovementAllowed = this.taskSelectPipe.transform(
        plannedpbmTask,
        TBTabName.PlannedPBM,
        this.movedTaskBLId
      );
    });
  }

  /**
   * Methods resets the controls for task movement which includes
   * target BL and route list dropdown & reset, and submit button
   */
  resetMoveToControls() {
    this.isResetDisabled = true;
    this.isSubmitDisabled = true;
    this.isPullbackDisabled = true;
    this.isUpdateStateDisable = true;
    this.isTargetBLSelectionDisabled = true;
    this.isTargetRouteSelectionDisabled = true;
  }

  /**
   * Methods fetches the route list for given selected target BL
   * @param businessLocationId selected target business location
   */
  getRouteListForBL(businessLocationId: string) {
    this.taskInfoService.loading = true;
    this.dataAccessService.getRouteListData(businessLocationId, undefined).subscribe({
      next: (res: RouteListResponse) => {
        this.blAllRoutes = res;
      },
      error: () => {
        this.taskInfoService.loading = false;
      },
      complete: () => {
        this.formatTargetRouteList();
        this.taskInfoService.loading = false;
      },
    });
  }

  /**
   * Method formats the target route list for dropdown selection
   * It also sorts the route list by alphabatically order
   */
  formatTargetRouteList() {
    let movedTaskList = [...this.dataRepositoryService.movingTasksMap.values()];
    const selectedTab = this.taskInfoService.selectedTabName.toLowerCase();
    this.isMoveToOnRouteAllowed = this.canTaskMove(selectedTab, movedTaskList);
    let checkRouteList: SelectionOption[] = this.checkTargetRouteState(
      this.blAllRoutes.routes
    )?.map((route: BLRoutesList) => ({
      id: route.route_id,
      value: route.route_label ?? '',
    }));
    let filteredRoutesList: SelectionOption[] = checkRouteList;
    if(selectedTab !== TBTabName.Unloaded && selectedTab !== TBTabName.PushToCloud) {
      filteredRoutesList = checkRouteList
      ?.slice()
      ?.filter(
        (f: SelectionOption) =>
          !movedTaskList?.map((m) => m.route_id)?.includes(f.id)
      );
    }
    this.targetRouteSelectionList = [];
    this.targetRouteSelectionList = this.dataFormatService.sortByProperty(
      filteredRoutesList,
      "value"
    );
  }

  /**
   * This method would determine if the task is allowed for movement to particular route
   * @param selectedTab - tab name of selected tab
   * @param movedTaskList - selected task list for movement
   * @returns true or false if the task is allowed for movement
   */
  canTaskMove(selectedTab: string, movedTaskList: MovingTaskEntry[]) {
    let isMovementAllowed: boolean;
    switch (selectedTab) {
      case TBTabName.PushToCloud:
        isMovementAllowed = true;
        break;
      case TBTabName.Unloaded:
        isMovementAllowed = false;
        break;
      default:
        isMovementAllowed = this.checkTaskSource(movedTaskList);
        break;
    }
    return isMovementAllowed;
  }

  /**
   * Method checks the task source as if it is a PBM sourced task, then on-route target route movement is allowed
   * else the task should be verified based on its type.
   * @param movedTaskList List of task which are selected for task movement
   * @returns boolean value
   */
  checkTaskSource(movedTaskList: MovingTaskEntry[]): boolean {
    let isOnRouteAllowed = false;
    //check for all task if PBM sourced or not
    isOnRouteAllowed = movedTaskList.every((x: MovingTaskEntry) => x.source.toLowerCase() === TaskSource.PBM)
    if(!isOnRouteAllowed){
      movedTaskList?.forEach((x: MovingTaskEntry) => {
        //non-PBM sourced task should be checked by their type
        if (x?.source?.toLowerCase() != TaskSource.PBM) {
          isOnRouteAllowed = this.checkTaskType(x);
        }
      });
    }

    return isOnRouteAllowed;
  }

  /**
   * Checks if all tasks in the given list are from PBM source.
   * @param taskList List of task which are selected for task movement
   * @returns boolean value
   */
  isPBMSource(taskList: Array<{ source?: string }>): boolean {
    return taskList.every((task) =>
    task?.source?.toLowerCase() === TaskSource.PBM);
  }

  /**
   * Method checks the task type as if it is of delivery, visit, handover, emptymailbox, pickup or combination of these
   * then return false as these task type can not be moved to On-Route state route
   * REASON: These tasks are NON-PBM source task.
   * -Pickups from EDI for DK,
   * -Deliveries and handovers from CSP for DK and SE,
   * -Visits and Empty mailbox from POL
   * @param movedTaskList List of task which are selected for task movement
   * @returns boolean value
   */

  checkTaskType(movedTask: MovingTaskEntry): boolean {
    let isOnRouteAllowed = true;
    //add all task type
    switch (movedTask?.task_type?.toLowerCase()) {
      case TaskType.Deliver:
      case TaskType.Visit:
      case TaskType.Handover:
      case TaskType.Pickup:
      case TaskType.Emptymailbox:
        isOnRouteAllowed = false;
        break;
    }
    return isOnRouteAllowed;
  }

  /**
   * Method takes the route list and filters out the completed state route, and returns the final
   * route list.
   * @param routeList list of all the routes
   * @returns the filtered list of route excluding completed state route
   */
  checkTargetRouteState(routeList: BLRoutesList[]): BLRoutesList[] {
    if (!this.isMoveToOnRouteAllowed) {
      return routeList?.filter(
        (f: BLRoutesList) =>
          f?.route_state?.toLowerCase() !== RouteState.Completed &&
          f?.route_state?.toLowerCase() !== RouteState.Onroute &&
          f?.route_state?.toLowerCase() != "" &&
          f?.route_state?.toLowerCase() !== RouteState.Finishloading
      );
    }
    return routeList?.filter(
      (f: BLRoutesList) =>
        f?.route_state?.toLowerCase() !== RouteState.Completed &&
        f?.route_state?.toLowerCase() != ""
    );
  }

  /**
   * Method gets route id and forms taskMovementData object
   * request payload for server, object contains GUID, command type, target route-id
   * moving task ids, timestamp and user id
   * @param targetRouteId selected target route id
   */
  onTargetRouteSelection(targetRouteId: string | undefined) {
    if (!!targetRouteId && targetRouteId != "Move to" && targetRouteId != "") {
      //create task move object
      this.dataRepositoryService.taskMovementData = {
        id: crypto.randomUUID(),
        command_type: "",
        route_id: targetRouteId,
        timestamp: new Date(),
        user_id: "",
        bl_id: this.selectedTargetBL?.id
      };
      //enable submit button
      this.isSubmitDisabled = false;
      this.isPullbackDisabled = true;
      this.selectedTargetRoute = {
        id: targetRouteId,
        value: this.targetRouteSelectionList.find(f=>f.id==targetRouteId)?.value??''
      }
    }
  }

  /**
   * Method call route list for selected BL id
   * @param targetBLId selected target business location id
   */
  onTargetBLSelection(targetBLId: string | undefined) {
    if (!!targetBLId && targetBLId == "Move to" || targetBLId == "") {
      return;
    }

    this.blState = BLState.UserChanged;
    this.selectedTargetRoute = {} as SelectionOption | undefined;
    this.dataRepositoryService.taskMovementData.route_id = "";
    this.setSelectedTargetBL(targetBLId??'');

    if (this.taskInfoService.selectedTabName.toLowerCase() === TBTabName.Unplannedpickup) {
      this.handleUnplannedTargetBLSelection(targetBLId??'');
      return;
    }

    this.handleDefaultTargetBLSelection(targetBLId??'');
  }

  /**
   * getting unplan task command information to data repository of pullBacktaskMovementData
   */
    getPullBackCommand() {
      this.dataRepositoryService.pullBacktaskMovementData = {
        id: crypto.randomUUID(),
        command_type: CommandType.PullBackTaskCommand,
        task_id: this.dataRepositoryService.movingTasksMap,
        timestamp: new Date(),
        user_id: '',
        bl_id: this.selectedTargetBL?.id
      }
    } 

  /**
   * Method posts the task movement object to server. It call delta APIs after
   * receiving the response from backend post movement.
   */
  onSubmitTaskMovement() {
    if (
      this.dataRepositoryService.taskMovementData ||
      this.dataRepositoryService.pullBacktaskMovementData
    ) {
      const selectedTab = this.taskInfoService.selectedTabName.toLowerCase();
      let taskMoveObject;
      switch (selectedTab) {
        case TBTabName.Planned:
        case TBTabName.Unplannedpickup:
        case TBTabName.PlannedPBM:
          taskMoveObject = this.dataRepositoryService.taskMovementData?.route_id ? this.formatTaskMoveObject() : this.formatpullBackMoveObject();
          break;
        case TBTabName.Unloaded:
          taskMoveObject = this.formatUnloadedItems();
          break;
        case TBTabName.PushToCloud:
          taskMoveObject = this.formatTaskMoveObject();
          break;
        default:
          break;
      }
      try {
        this.connectionService.httpReq({
          reqType: AppConstants.POSTREQUEST,
          endUrl: AppConstants.Command,
          directoryType: AppConstants.DP,
          data: taskMoveObject
        })
          .subscribe({
            next: () => {
            this.refereshTabsData();
            const response: TaskMovementResult = {
              success: true,
              message: this.getMovementSuccessMessage()
            };
            this.taskMovementSuccess.next(response);
            this.resetTaskMovement();
          },
          error: (error: { status?: number; detail?: string }) => {
            const response: TaskMovementResult = {
              success: false,
              message: this.getMovementErrorMessage(error.status, error?.detail)??ErrorMessage.BASEERRORMESSAGE
            };
            this.taskMovementSuccess.next(response);
          }
        });
    } catch (err) {
          const response = {
            success: false,
            message: this.getMovementErrorMessage()??ErrorMessage.BASEERRORMESSAGE
          };
          this.taskMovementSuccess.next(response);
        }
    }
  }

    /**
   * To Show success Message After Task Movement  Successful
   * @returns Based on which tab is selected
   * to show the item/task movement
   */
  private getMovementSuccessMessage(): string {
    let successMessage = "";

    if (this.taskInfoService.selectedTabName === TBTabName.Unloaded) {
      successMessage = ErrorMessage.ITEMMOVEMENTSUCCESS;
    } else {
      successMessage = ErrorMessage.TASKMOVEMENTSUCCESS;
    }
    const routeId = this.dataRepositoryService?.taskMovementData?.route_id;
    const route = this?.targetRouteSelectionList?.find(
      (r) => r.id === routeId
    );
    const routeName = route?.value ?? "";

    if (routeName) {
      successMessage = successMessage.replace(
        "successful.",
        `successful to route "${routeName}".`
      );
    }
    return successMessage;
  }

  /**
   * To show failure message after Task Movement
   * @returns Based on which tab is selected
   * to show the item/task movement
   */
  private getMovementErrorMessage(statusCode?: number, errorDetails?:string): string   {
    if(statusCode && statusCode === HttpStatusCode.Forbidden){
      return ErrorMessage.RESTRICTIONMESSAGE;
    }
    if (this.taskInfoService.selectedTabName === TBTabName.Unloaded) {
      return errorDetails ?? ErrorMessage.ITEMMOVEMENTERROR;
    } else {
      return errorDetails ?? ErrorMessage.TASKMOVEMENTERROR;
    }
  }

  
  refereshTabsData() {
    const selectedTab = this.taskInfoService.selectedTabName.toLowerCase();
    //check for PBM task then no source route id is there
    switch (selectedTab) {
      case TBTabName.Planned:
        this.isTaskMovementDone = true;
        this.updateWithDeltaPostTaskMovement();
        break;
      case TBTabName.Unplannedpickup:
        this.isTaskMovementDone = true;
        this.updatePlannedPBMDeltaPostTaskMovement();
        if (
          this.checkTargetRouteInPlannedTab(
            this.dataRepositoryService.taskMovementData.route_id
          )
        ) {
          this.updateWithDeltaPostTaskMovement();
          this.updatePlannedDeltaPostTaskMovement();
        } else {
          this.updateWithDeltaPostTaskMovement();
        }
        break;
      case TBTabName.PlannedPBM:
        this.isTaskMovementDone = true;
        this.updateWithDeltaPostTaskMovement();
        this.updateUnplannedDeltaPostTaskMovement();
        break;
      case TBTabName.Unloaded:
        if (
          this.checkTargetRouteInPlannedTab(
            this.dataRepositoryService.taskMovementData.route_id
          )
        ) {
          this.updateWithDeltaPostTaskMovement();
          this.updatePlannedDeltaPostTaskMovement();
        } else {
          this.updateWithDeltaPostTaskMovement();
        }
        break;
      case TBTabName.PushToCloud:
        if (
          this.checkTargetRouteInPlannedTab(
            this.dataRepositoryService.taskMovementData.route_id
          )
        ) {
          this.updateWithDeltaPostTaskMovement();
          this.updatePlannedDeltaPostTaskMovement();
        } else {
          this.updateWithDeltaPostTaskMovement();
        }
        break;
      default:
        break;
    }
  }

  /**
   *
   * format of task for task movement from planned and unplanned pickup
   * @returns task move object for planned and unplanned tab
   */
  formatTaskMoveObject() {
    return [
      {
        ...this.dataRepositoryService.taskMovementData,
        task_id: [...this.dataRepositoryService.movingTasksMap.values()].map(
          (m) => m.task_id
        ),
        timestamp: new Date(),
        user_id: this.dataRepositoryService.loggedInUser?.username ?? "",
        command_type: this.dataRepositoryService.taskMovementData?.route_id ? CommandType.MoveTaskCommand : CommandType.PullBackTaskCommand,
        bl_id: this.selectedTargetBL?.id
      },
    ];
  }
  /**
   *
   * format of task for task movement from planned PBM
   * @returns  task move objects for plannedpbm tab
   */
  formatpullBackMoveObject() {
    this.getPullBackCommand();
    return [
      {
        ...this.dataRepositoryService.pullBacktaskMovementData,
        task_id: [...this.dataRepositoryService.movingTasksMap.values()].map(
          (m) => m.task_id
        ),
        timestamp: new Date(),
        user_id: this.dataRepositoryService.loggedInUser?.username ?? "",
      },
    ];
  }

  /**
   *
   * format of items for task movement for unloaded and push to cloud items
   * @returns  item move objects for unloaded items
   */
  formatUnloadedItems() {
    return [
      {
        ...this.dataRepositoryService.taskMovementData,
        item_id: [...this.dataRepositoryService.movingTasksMap.values()].map(
          (m) => m.item_id
        ),
        timestamp: new Date(),
        user_id: this.dataRepositoryService.loggedInUser?.username ?? "",
        command_type: CommandType.MoveItemCommand,
      },
    ];
  }
  /**
   * Methods updates the data by delta call post the task movement
   */
  updateWithDeltaPostTaskMovement() {
    this.taskInfoService.activeTabSubscription();
    this.dataAccessService.getRouteInfoDeltaOnce();
  }

  /**
   * This method is to update the tasks in planned tab according to the task movement.
   */
  updatePlannedDeltaPostTaskMovement() {
    this.taskInfoService.getPlannedDeltaOnce();
  }
  /**
   * This method is to update the tasks in Unplanned pickup tab according to the task movement.
   */
  updateUnplannedDeltaPostTaskMovement() {
    this.taskInfoService.getUnplannedPickupDeltaOnce();
  }
  /**
   * This method is to update the tasks in Planned PBM tab according to the task movement.
   */
  updatePlannedPBMDeltaPostTaskMovement() {
    this.taskInfoService.getPlannedPBMDeltaOnce();
  }

  /**
   * Method checks if the target route is also selected in planned tab
   */
  checkTargetRouteInPlannedTab(targerRouteId: string) {
    return !!this.dataRepositoryService.selectedRouteList.find(
      (f) => f.route_id === targerRouteId
    );
  }

  /**
   * Reset button action on TB
   */
  resetTaskMovement(preserveMapHighlight = false) {
    // Route re-selection keeps the retained tasks highlighted, so skip wiping the map registry.
    if (!preserveMapHighlight) {
      this.resetOnMapView();
    }
    this.selectedTargetRoute = {} as SelectionOption;
    this.selectedTargetBL = undefined;
    this.isTaskMovementTriggered = false;
    this.isSelectedAllTasksTriggered = false;
    this.isSelectedAll = false;
    this.isMoveToOnRouteAllowed = true;
    this.selectedOptionsForTaskMove = [];
    this.taskmoveCount.set(0);
    this.blAllRoutes = { routes: [] };
    this.dataRepositoryService.taskMovementData = {} as TaskMoveCommand;
    this.dataRepositoryService.pullBacktaskMovementData =
      {} as PullBackTaskCommand;
    this.dataRepositoryService.movingTasksMap.clear();
    this.taskMovementEnablerTab = "";
    this.movedTaskBLId = "";
    this.businessLocationsList = [];
    this.resetBtnAction = TBResetAction.TaskMovement;
    this.resetMoveToControls();
    this.showSelectedTaskKPI = false;
    this.selectedTaskKPI = [];

    //reformat the other task based on the unselection of task
    if (
      this.taskInfoService.selectedTabName.toLowerCase() ===
        TBTabName.Unplannedpickup &&
      this.taskInfoService.selectedUnplannedFavList?.id !== "default"
    ) {
      this.reformatOtherTasks();
    }
    if (
      this.taskInfoService.selectedTabName.toLowerCase() ===
        TBTabName.PlannedPBM &&
      this.taskInfoService.selectedPlannedFavList?.id !== "default"
    ) {
      this.reformatPlannedPBMTasks();
    }
  }

  resetOnMapView(){
    if (
      (this.router.url === Navigation.MapLeaflet || this.router.url === Navigation.Landing) 
    ) {
      this.resetButtonClicked.next(true);
      // Keep the collection flag in sync with highlightedTaskOnMap so a later toggle re-highlights instead of clearing.
      this.dataRepositoryService.highlightedTaskOnMap.forEach((taskId) => {
        const collectionTask = this.dataRepositoryService.plannedTbCollection.get(taskId);
        if (collectionTask) {
          collectionTask.isSelectedByMap = false;
        }
      });
      this.dataRepositoryService.highlightedTaskOnMap.clear();
    }
  }

  /**
   * Communication data stream abstract
   */
  extractDataStream(dataStream: CommunicationDataStream) {
    //check the type of activity present in dataStream object and exercute appropriate case
    switch (dataStream.type) {
      case TBDataSteamType.Reset: {
        this.resetTaskMovement();
        break;
      }
      case TBDataSteamType.RemoveTask: {
        /**
         * check the input route ID with route IDs present in task movement list
         * and remove those tasks from the list
         */
        const routeIds = new Set(
          Array.isArray(dataStream.data) ? dataStream.data : [dataStream.data]
        );
        const movedTaskList = [
          ...this.dataRepositoryService.movingTasksMap.values(),
        ] as MovingTaskEntry[];
        movedTaskList?.forEach((moveTask: MovingTaskEntry) => {
          if (routeIds.has(moveTask.route_id)) {
            this.removeSelectedTask(moveTask.task_id ?? '');
          }
        });
        // update the task movement count after removing the tasks from movement list based on route id
        this.taskmoveCount.set(this.selectedOptionsForTaskMove.filter((f) => f.isMovementAllowed).length);
        break;
      }
      default:
    }
  }

  /**
   * 
   * Method created the object for selected rows KPI which are show in View
   */
  setSelectedRowKPI(){
    let stopGroups = this.taskInfoFormatterService.groupCollection(this.selectedOptionsForTaskMove, "stop_id");
    let kpi: SelectedTBRowKPI = {
      stops: stopGroups?.length??0,
      tasks: this.selectedOptionsForTaskMove.length,
      task_type: this.getSelectedTaskTypes(),
      total_volume: this.selectedOptionsForTaskMove.reduce((acc,curr)=>{ return  acc + ((curr as any)?.volume??0) },0).toFixed(2),
      total_weight: this.selectedOptionsForTaskMove.reduce((acc,curr)=>{ return  acc + ((curr as any)?.weight??0) },0).toFixed(2),
      goods_count: this.selectedOptionsForTaskMove.reduce((acc,curr)=>{ return  acc + ((curr as any)?.goods_count??0) },0),
      item_count: this.selectedOptionsForTaskMove.reduce((acc,curr)=>{ return  acc + ((curr as any)?.item_count??0) },0)
    }
    this.selectedTaskKPI = Object.entries(kpi).map(([key, value]: [string, string | number])=>({key: key as keyof SelectedTBRowKPI, value}));
    
  }

  /**
   * Method creates the group of task ype by categorizing and 
   * obtained counts of each type and forms a string as output
   * @returns the formatted string
   */
  getSelectedTaskTypes(){
    let selectedTaskType = this.selectedOptionsForTaskMove.map(m => (m as any).task_type).join(',').toLowerCase();
    const typeCategory = selectedTaskType.split(',').map(category=>category.trim().toLowerCase());
    let catergoryCounter: { [key: string]: number } = {}

    catergoryCounter = typeCategory.reduce((accumulator: { [key: string]: number }, currentWord: string) => {
      accumulator[currentWord] = (accumulator[currentWord] || 0) + 1;
      return accumulator;
    }, {});
    
    const formattedOutput = Object.entries(catergoryCounter).map(([action, count]: [string, number]) => {
      return `${count} ${this.titleCasePipe.transform(action)}${count > 1 ? 's' : ''}`;
    });

    return formattedOutput.join(', ');
  }

  /**
   * Methos toggle the flip icon rotation on click using 
   * a flag variable
   */
  toggleIconRotation(){
    this.showSelectedTaskKPI = !this.showSelectedTaskKPI;
  }

  /**
   * Method updates the selected task list with delta updates, 
   * if the task should be removed from the selection list after delta update
   */
  updateTaskSelectionPostDelta(){
    const dataSet = this.taskInfoService.getSelectedTabValues();
    const objectIdentifier =
      this.taskInfoService.selectedTabName.toLowerCase() !=
      TBTabName.Unloaded.toLowerCase()
        ? "task_id"
        : "item_id";

    //check if delta has updated the selected task entry (removing the task from the route)
    this.selectedOptionsForTaskMove = this.selectedOptionsForTaskMove.filter(
      (f) =>
        dataSet.some((s) => (f as object as Record<string, string | undefined>)[objectIdentifier] == (s as object as Record<string, string | undefined>)[objectIdentifier])
    );
    //filter the restricted task out of the selected task collection.
    this.selectedOptionsForTaskMove = this.selectedOptionsForTaskMove.filter(
      (f) => f.isMovementAllowed
    );
    this.taskmoveCount.set(this.selectedOptionsForTaskMove.length);
  }


}
